import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Calendar as CalendarIcon, ChevronDown, X, Plus, Info, Layers, CheckCircle2 } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { TaskFormData } from "./TaskCreationModal";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { TagAutocomplete } from "./TagAutocomplete";
import { AssigneeAutocomplete } from "./AssigneeAutocomplete";
import { TagManagementModal } from "./TagManagementModal";
import { AssigneeManagementModal } from "./AssigneeManagementModal";
import { MemberAssignmentSelector } from "./MemberAssignmentSelector";
import { CustomFieldsEditor } from "./CustomFieldsEditor";
import { categoryHasPhases, getPhaseTemplateByCategory } from "@/lib/phaseTemplates";

interface TaskDetailsFormProps {
  formData: TaskFormData;
  onUpdate: (updates: Partial<TaskFormData>) => void;
  unaId?: string;
  taskCategory?: string;
}

export const TaskDetailsForm = ({ formData, onUpdate, unaId: propUnaId, taskCategory }: TaskDetailsFormProps) => {
  const { user } = useAuth();
  const [unaId, setUnaId] = useState<string | undefined>(propUnaId);
  const [relatedItemType, setRelatedItemType] = useState<string>("");
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [relatedOpen, setRelatedOpen] = useState(false);
  const [phasesOpen, setPhasesOpen] = useState(false);
  const [assets, setAssets] = useState<any[]>([]);
  const [members, setMembers] = useState<any[]>([]);
  const [contributions, setContributions] = useState<any[]>([]);
  const [resolutions, setResolutions] = useState<any[]>([]);
  const [showTagManagement, setShowTagManagement] = useState(false);
  const [showAssigneeManagement, setShowAssigneeManagement] = useState(false);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>({});

  // Update parent form data when custom fields change
  useEffect(() => {
    if (Object.keys(customFieldValues).length > 0) {
      onUpdate({ customFields: customFieldValues });
    }
  }, [customFieldValues]);

  useEffect(() => {
    if (user) {
      fetchRelatedItems();
      loadNotificationPreferences();
    }
  }, [user]);

  const loadNotificationPreferences = async () => {
    if (!user) return;
    
    try {
      const { data: una } = await supabase
        .from('unas')
        .select('id')
        .eq('treasury_secretary_id', user.id)
        .maybeSingle();

      if (una) {
        const { data: prefs } = await supabase
          .from('notification_preferences')
          .select('*')
          .eq('user_id', user.id)
          .eq('una_id', una.id)
          .maybeSingle();
        
        if (prefs) {
          onUpdate({
            notifications: {
              reminder: prefs.email_day_before_due,
              notifyAssignees: prefs.email_on_task_assigned,
            }
          });
        }
      }
    } catch (error) {
      console.error('Error loading notification preferences:', error);
    }
  };

  const fetchRelatedItems = async () => {
    if (!user) return;
    
    try {
      const { data: una } = await supabase
        .from('unas')
        .select('id')
        .eq('treasury_secretary_id', user.id)
        .maybeSingle();

      if (una) {
        setUnaId(una.id);
        // Fetch assets
        const { data: assetsData } = await supabase
          .from('assets')
          .select('id, description')
          .eq('una_id', una.id);
        setAssets(assetsData || []);

        // Fetch members
        const { data: membersData } = await supabase
          .from('una_members')
          .select('id, full_name')
          .eq('una_id', una.id);
        setMembers(membersData || []);

        // Fetch contributions
        const { data: contributionsData } = await supabase
          .from('contributions')
          .select('id, description')
          .eq('una_id', una.id);
        setContributions(contributionsData || []);

        // Fetch resolutions
        const { data: resolutionsData } = await supabase
          .from('resolutions')
          .select('id, title')
          .eq('una_id', una.id);
        setResolutions(resolutionsData || []);
      }
    } catch (error) {
      console.error('Error fetching related items:', error);
    }
  };


  const addRelatedItem = (itemId: string) => {
    if (!relatedItemType || !itemId) return;
    
    const currentRelated = formData.related_items || {};
    const typeItems = currentRelated[relatedItemType] || [];
    
    if (!typeItems.includes(itemId)) {
      onUpdate({
        related_items: {
          ...currentRelated,
          [relatedItemType]: [...typeItems, itemId]
        }
      });
    }
  };

  const removeRelatedItem = (type: string, itemId: string) => {
    const currentRelated = formData.related_items || {};
    onUpdate({
      related_items: {
        ...currentRelated,
        [type]: (currentRelated[type] || []).filter((id: string) => id !== itemId)
      }
    });
  };

  const getRelatedItemName = (type: string, itemId: string) => {
    let items: any[] = [];
    let nameField = 'name';
    
    switch (type) {
      case 'assets':
        items = assets;
        nameField = 'description';
        break;
      case 'members':
        items = members;
        nameField = 'full_name';
        break;
      case 'contributions':
        items = contributions;
        nameField = 'description';
        break;
      case 'resolutions':
        items = resolutions;
        nameField = 'title';
        break;
    }
    
    const item = items.find(i => i.id === itemId);
    return item ? item[nameField] : 'Unknown';
  };

  return (
    <div className="space-y-6">
      {/* Task Title */}
      <div className="space-y-2">
        <Label htmlFor="task_title" className="text-base">
          ✏️ Task Title <span className="text-red-500">*</span>
        </Label>
        <Input
          id="task_title"
          value={formData.task_title}
          onChange={(e) => onUpdate({ task_title: e.target.value })}
          placeholder="Enter task title..."
          className="text-base"
          maxLength={200}
        />
      </div>

      {/* Description */}
      <div className="space-y-2">
        <Label htmlFor="task_description" className="text-base">
          📝 Description
        </Label>
        <Textarea
          id="task_description"
          value={formData.task_description}
          onChange={(e) => onUpdate({ task_description: e.target.value })}
          placeholder="Describe what needs to be done..."
          rows={4}
          className="text-base min-h-[120px]"
        />
      </div>

      {/* Due Date */}
      <div className="space-y-2">
        <Label className="text-base">📅 Due Date (Optional)</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className={cn(
                "w-full justify-start text-left font-normal",
                !formData.due_date && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="mr-2 h-4 w-4" />
              {formData.due_date ? format(formData.due_date, "MMM dd, yyyy") : "Pick a date"}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0">
            <Calendar
              mode="single"
              selected={formData.due_date}
              onSelect={(date) => onUpdate({ due_date: date })}
              initialFocus
              className="pointer-events-auto"
            />
          </PopoverContent>
        </Popover>
        {formData.due_date && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onUpdate({ due_date: undefined })}
            className="text-xs"
          >
            Clear date
          </Button>
        )}
      </div>

      {/* Member Assignment - New UNA Member-based Assignment */}
      {unaId && (
        <MemberAssignmentSelector
          unaId={unaId}
          selectedMemberIds={formData.assignedMemberIds || []}
          onSelectionChange={(memberIds) => onUpdate({ assignedMemberIds: memberIds })}
          taskCategory={taskCategory || formData.category_type || undefined}
          required={true}
        />
      )}

      {/* Legacy Assign To (fallback for non-UNA tasks) */}
      {!unaId && (
        <AssigneeAutocomplete
          selectedAssignees={formData.assignees || []}
          onAssigneesChange={(assignees) => onUpdate({ assignees })}
          unaId={unaId}
          onManageClick={() => setShowAssigneeManagement(true)}
        />
      )}


      {/* Tags with Autocomplete */}
      <TagAutocomplete
        selectedTags={formData.tags || []}
        onTagsChange={(tags) => onUpdate({ tags })}
        unaId={unaId}
        onManageClick={() => setShowTagManagement(true)}
      />

      {/* Workflow Phases - Optional Configuration */}
      {formData.category_type && categoryHasPhases(formData.category_type) && (
        <Collapsible open={phasesOpen} onOpenChange={setPhasesOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="outline" className="w-full justify-between">
              <span className="flex items-center gap-2">
                <Layers className="h-4 w-4" />
                Workflow Phases (Optional)
              </span>
              <ChevronDown className={cn("h-4 w-4 transition-transform", phasesOpen && "rotate-180")} />
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent className="space-y-4 pt-4">
            {(() => {
              const phaseTemplate = getPhaseTemplateByCategory(formData.category_type!);
              
              return (
                <div className="space-y-4">
                  <div className="rounded-lg bg-muted/50 p-4">
                    <div className="flex items-start gap-3">
                      <div className="p-2 rounded-full bg-primary/10">
                        <Layers className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium mb-1">Workflow Phases Available</h4>
                        <p className="text-sm text-muted-foreground">
                          This task category has a {phaseTemplate?.phases.length}-phase workflow that helps you track progress through sequential steps.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-3">
                    <button
                      type="button"
                      onClick={() => onUpdate({ usePhases: 'standard' })}
                      className={cn(
                        "group p-4 rounded-lg border-2 text-left transition-all",
                        "hover:border-primary hover:bg-primary/10 hover:shadow-md",
                        formData.usePhases === 'standard' 
                          ? "border-primary bg-primary/10 shadow-md ring-2 ring-primary/20" 
                          : "border-border"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className={cn(
                          "h-5 w-5 transition-all",
                          formData.usePhases === 'standard' ? "text-primary scale-110" : "text-muted-foreground group-hover:text-primary"
                        )} />
                        <div>
                          <div className="font-medium">Use Standard Phases</div>
                          <div className="text-sm text-muted-foreground">
                            Apply the recommended {phaseTemplate?.phases.length}-phase workflow with pre-defined requirements
                          </div>
                        </div>
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() => onUpdate({ usePhases: 'skip' })}
                      className={cn(
                        "group p-4 rounded-lg border-2 text-left transition-all",
                        "hover:border-primary hover:bg-primary/10 hover:shadow-md",
                        formData.usePhases === 'skip' 
                          ? "border-primary bg-primary/10 shadow-md ring-2 ring-primary/20" 
                          : "border-border"
                      )}
                    >
                      <div className="flex items-center gap-3">
                        <CheckCircle2 className={cn(
                          "h-5 w-5 transition-all",
                          formData.usePhases === 'skip' ? "text-primary scale-110" : "text-muted-foreground group-hover:text-primary"
                        )} />
                        <div>
                          <div className="font-medium">Skip Phases</div>
                          <div className="text-sm text-muted-foreground">
                            Create a simple task without phases (you can add them later)
                          </div>
                        </div>
                      </div>
                    </button>
                  </div>

                  {formData.usePhases === 'standard' && phaseTemplate && (
                    <div className="mt-4 space-y-3">
                      <h4 className="font-medium text-sm">Phase Preview:</h4>
                      {phaseTemplate.phases.map((phase, idx) => (
                        <div key={idx} className="p-3 rounded-lg bg-muted/30 border">
                          <div className="flex items-start gap-3">
                            <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/20 text-xs font-semibold text-primary">
                              {phase.phase_number}
                            </div>
                            <div className="flex-1">
                              <div className="font-medium text-sm">{phase.phase_name}</div>
                              <div className="text-xs text-muted-foreground">{phase.phase_description}</div>
                              <div className="mt-2 flex items-center gap-4 text-xs text-muted-foreground">
                                <span>{phase.requirements.length} manual requirements</span>
                                {phase.document_requirements.length > 0 && (
                                  <span>{phase.document_requirements.length} documents</span>
                                )}
                                {phase.signature_requirements.length > 0 && (
                                  <span>{phase.signature_requirements.length} signatures</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}
          </CollapsibleContent>
        </Collapsible>
      )}

      {/* Related Items */}
      <Collapsible open={relatedOpen} onOpenChange={setRelatedOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="outline" className="w-full justify-between">
            <span>🔗 Related Items (Optional)</span>
            <ChevronDown className={cn("h-4 w-4 transition-transform", relatedOpen && "rotate-180")} />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-4">
          <div className="space-y-3">
            <div className="flex gap-2">
              <Select value={relatedItemType} onValueChange={setRelatedItemType}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="assets">Asset</SelectItem>
                  <SelectItem value="members">Member</SelectItem>
                  <SelectItem value="contributions">Contribution</SelectItem>
                  <SelectItem value="resolutions">Resolution</SelectItem>
                </SelectContent>
              </Select>
              
              {relatedItemType && (
                <Select onValueChange={addRelatedItem}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select item" />
                  </SelectTrigger>
                  <SelectContent>
                    {relatedItemType === 'assets' && assets.map(asset => (
                      <SelectItem key={asset.id} value={asset.id}>{asset.description}</SelectItem>
                    ))}
                    {relatedItemType === 'members' && members.map(member => (
                      <SelectItem key={member.id} value={member.id}>{member.full_name}</SelectItem>
                    ))}
                    {relatedItemType === 'contributions' && contributions.map(contrib => (
                      <SelectItem key={contrib.id} value={contrib.id}>{contrib.description}</SelectItem>
                    ))}
                    {relatedItemType === 'resolutions' && resolutions.map(res => (
                      <SelectItem key={res.id} value={res.id}>{res.title}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Display selected related items */}
            {formData.related_items && (
              <div className="space-y-2">
                {Object.entries(formData.related_items).map(([type, items]) => (
                  (items as string[]).length > 0 && (
                    <div key={type} className="flex flex-wrap gap-2">
                      {(items as string[]).map((itemId: string) => (
                        <Badge key={itemId} variant="outline" className="gap-1">
                          {type.slice(0, -1)}: {getRelatedItemName(type, itemId)}
                          <X
                            className="h-3 w-3 cursor-pointer"
                            onClick={() => removeRelatedItem(type, itemId)}
                          />
                        </Badge>
                      ))}
                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Advanced Options */}
      <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
        <CollapsibleTrigger asChild>
          <Button variant="outline" className="w-full justify-between">
            <span>⚙️ Advanced Options</span>
            <ChevronDown className={cn("h-4 w-4 transition-transform", advancedOpen && "rotate-180")} />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-4 pt-4">
          {/* Estimated Hours */}
          <div className="space-y-2">
            <Label htmlFor="estimated_hours" className="text-base">
              ⏱️ Estimated Time
            </Label>
            <div className="flex items-center gap-2">
              <Input
                id="estimated_hours"
                type="number"
                min="0"
                step="0.5"
                value={formData.estimated_hours || ""}
                onChange={(e) => onUpdate({ estimated_hours: e.target.value ? parseFloat(e.target.value) : undefined })}
                placeholder="2.5"
                className="w-24"
              />
              <span className="text-muted-foreground">hours</span>
            </div>
          </div>

          {/* Privacy */}
          <div className="space-y-2">
            <Label className="text-base">🔒 Privacy</Label>
            <RadioGroup
              value={formData.privacy || "all_members"}
              onValueChange={(value) => onUpdate({ privacy: value })}
              className="space-y-2"
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="all_members" id="privacy-all" />
                <Label htmlFor="privacy-all" className="cursor-pointer">
                  Visible to all UNA members
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="assignees_only" id="privacy-assignees" />
                <Label htmlFor="privacy-assignees" className="cursor-pointer">
                  Visible only to assignees
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="private" id="privacy-private" />
                <Label htmlFor="privacy-private" className="cursor-pointer">
                  Private (only me)
                </Label>
              </div>
            </RadioGroup>
          </div>

          {/* Recurring Task */}
          <div className="space-y-2">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="recurring"
                checked={!!formData.recurring_config?.enabled}
                onChange={(e) => onUpdate({
                  recurring_config: {
                    ...formData.recurring_config,
                    enabled: e.target.checked
                  }
                })}
                className="h-4 w-4"
              />
              <Label htmlFor="recurring" className="cursor-pointer">
                🔁 Make this a recurring task
              </Label>
            </div>
            
            {formData.recurring_config?.enabled && (
              <div className="pl-6 space-y-2 pt-2">
                <div className="flex gap-2 items-center">
                  <span className="text-sm">Repeat:</span>
                  <Select
                    value={formData.recurring_config?.frequency || 'weekly'}
                    onValueChange={(value) => onUpdate({
                      recurring_config: {
                        ...formData.recurring_config,
                        frequency: value
                      }
                    })}
                  >
                    <SelectTrigger className="w-[140px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">Daily</SelectItem>
                      <SelectItem value="weekly">Weekly</SelectItem>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="yearly">Yearly</SelectItem>
                    </SelectContent>
                  </Select>
                  <span className="text-sm">every</span>
                  <Input
                    type="number"
                    min="1"
                    value={formData.recurring_config?.interval || 1}
                    onChange={(e) => onUpdate({
                      recurring_config: {
                        ...formData.recurring_config,
                        interval: parseInt(e.target.value)
                      }
                    })}
                    className="w-16"
                  />
                  <span className="text-sm">{formData.recurring_config?.frequency || 'week'}(s)</span>
                </div>
              </div>
            )}
          </div>

          {/* Notifications */}
          <div className="space-y-2">
            <Label className="text-base">📧 Notifications</Label>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="reminder"
                  checked={formData.notifications?.reminder || false}
                  onChange={(e) => onUpdate({
                    notifications: {
                      ...formData.notifications,
                      reminder: e.target.checked
                    }
                  })}
                  className="h-4 w-4"
                />
                <Label htmlFor="reminder" className="cursor-pointer text-sm">
                  Email reminder 1 day before due date
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="notify-assignees"
                  checked={formData.notifications?.notifyAssignees || false}
                  onChange={(e) => onUpdate({
                    notifications: {
                      ...formData.notifications,
                      notifyAssignees: e.target.checked
                    }
                  })}
                  className="h-4 w-4"
                />
                <Label htmlFor="notify-assignees" className="cursor-pointer text-sm">
                  Notify assignees when created
                </Label>
              </div>
            </div>
          </div>

          {/* Custom Fields - Category Specific */}
          {formData.category_type && (
            <CustomFieldsEditor
              category={formData.category_type}
              values={customFieldValues}
              onChange={setCustomFieldValues}
            />
          )}
        </CollapsibleContent>
      </Collapsible>

      {/* Management Modals */}
      <TagManagementModal
        open={showTagManagement}
        onOpenChange={setShowTagManagement}
        unaId={unaId}
      />
      <AssigneeManagementModal
        open={showAssigneeManagement}
        onOpenChange={setShowAssigneeManagement}
        unaId={unaId}
      />
    </div>
  );
};